import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink, Package, Truck } from "lucide-react";
import { Card } from "./ProfileCard";
import { VerifyBanner } from "./VerifyBanner";
import { useAccount } from "@/context/AccountContext";
import { accountApi } from "@/lib/api";
import { formatPrice } from "@/lib/site";

const fmtDate = (iso) => new Date(iso).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" });

const Tracking = ({ value }) =>
  /^https?:\/\//i.test(value) ? (
    <a href={value} target="_blank" rel="noreferrer" data-testid="account-order-tracking-link" className="inline-flex items-center gap-1.5 text-acid underline underline-offset-4">
      Traccia la spedizione <ExternalLink className="h-3.5 w-3.5" />
    </a>
  ) : (
    <span data-testid="account-order-tracking-code">Codice tracking: <span className="font-mono text-bone">{value}</span></span>
  );

const OrderCard = ({ order }) => {
  const shipped = Boolean(order.shipped_at);
  return (
    <li data-testid="account-order-card" className="border border-line bg-ink p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-mono text-sm text-acid">#{order.id.slice(-6).toUpperCase()}</p>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-smoke">{fmtDate(order.created_at)}</p>
        </div>
        <span data-testid="account-order-status" className={`flex items-center gap-1.5 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.15em] ${shipped ? "bg-acid text-ink" : "bg-ink-3 text-bone"}`}>
          {shipped ? <Truck className="h-3 w-3" /> : <Package className="h-3 w-3" />} {shipped ? "Spedito" : "In preparazione"}
        </span>
      </div>
      <ul className="mt-4 space-y-1 border-t border-line pt-4">
        {order.items.map((i) => (
          <li key={`${i.product_id}-${i.size}`} className="flex justify-between gap-4 text-sm">
            <span>{i.name} <span className="text-smoke">· {i.size} × {i.qty}</span></span>
            <span className="font-mono">{formatPrice(i.unit_price * i.qty)}</span>
          </li>
        ))}
      </ul>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-line pt-4 text-sm text-bone/70">
        <span>{shipped ? (order.tracking ? <Tracking value={order.tracking} /> : `Spedito il ${fmtDate(order.shipped_at)}`) : "Parte entro 48 ore dal pagamento"}</span>
        <span className="font-mono text-base text-bone">Totale {formatPrice(order.total)}</span>
      </div>
    </li>
  );
};

export const OrdersList = () => {
  const { customer } = useAccount();
  const { data, isLoading } = useQuery({
    queryKey: ["account-orders", customer.id, customer.email_verified],
    queryFn: () => accountApi.get("/account/orders").then((r) => r.data),
  });
  const orders = Array.isArray(data?.orders) ? data.orders : [];

  return (
    <Card title="I tuoi ordini" index="03" testid="account-orders-card">
      {!customer.email_verified ? (
        <VerifyBanner />
      ) : isLoading ? (
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-smoke">Caricamento…</p>
      ) : orders.length === 0 ? (
        <div data-testid="account-orders-empty" className="space-y-4">
          <p className="text-bone/70">Ancora nessun ordine con <span className="text-bone">{customer.email}</span>.</p>
          <Link to="/#sfushop" data-testid="account-orders-shop-link" className="inline-flex h-11 items-center rounded-full border border-bone/30 px-5 font-mono text-[11px] uppercase tracking-[0.18em] transition-colors hover:border-acid hover:text-acid">
            Scopri il drop
          </Link>
        </div>
      ) : (
        <ul className="space-y-4" data-testid="account-orders-list">{orders.map((o) => <OrderCard key={o.id} order={o} />)}</ul>
      )}
    </Card>
  );
};
